import * as React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useRef , useState , useEffect } from "react";
import gsap from "gsap";

export const EmailForm: React.FC = () => {
  const [email, setEmail] = React.useState("");
  const [error, setError] = React.useState("");
  const [videoId, setVideoId] = useState('');
  const [langId, setLangId] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');

  const parseId = (id: string) => {
    if (!id || id.length < 4) return null;
  
    const videoId = id[0];
    const langId = id[3];
  
    return { videoId, langId };
  };

  useEffect(() => {
    const id = searchParams.get('id');
    if (id) {
      const parsed = parseId(id);
      if (parsed) {
        const { videoId, langId } = parsed;
        setVideoId(videoId);
        setLangId(langId);
      }
    }
  }, [searchParams]);
  

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!validateEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }

    try {
      const response = await fetch('https://summit.creo-dev.com/api/summit-users', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email,
          variable_1: videoId,
          variable_2: langId 
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit email');
      }

      if (id) {
        navigate(`/final-avatar?id=${id}`);
      } else {
        navigate("/final-avatar");
      }
    } catch (error) {
      setError("Failed to submit email. Please try again.");
      console.error('Error submitting email:', error);
    }
  };
  const [isInitialized, setIsInitialized] = useState(false);
  const heartPath1Ref = useRef<SVGPathElement>(null);
  const heartPath2Ref = useRef<SVGPathElement>(null);
  const heartPath3Ref = useRef<SVGPathElement>(null);
  const heartPath4Ref = useRef<SVGPathElement>(null);

  useEffect(() => {
    setIsInitialized(true);
  }, []);

   // Hearts animation
   useEffect(() => {
    if (!isInitialized) return;
    
    // Store references to elements in local variables
    const heart1Element = heartPath1Ref.current;
    const heart2Element = heartPath2Ref.current;
    const heart3Element = heartPath3Ref.current;
    const heart4Element = heartPath4Ref.current;
    
    // Only run the animation on first load and on welcome page
    const animateHearts = () => {
      // First heart is always visible (we'll animate a slight pulse)
      if (heart1Element) {
        gsap.set(heart1Element, { 
          autoAlpha: 1,
          scale: 1
        });
        
        // Add a subtle pulse animation to the first heart
        gsap.to(heart1Element, {
          scale: 1.1,
          duration: 1.2,
          repeat: -1,
          yoyo: true,
          ease: "sine.inOut"
        });
      }
      
      // Other hearts will flow upward from the first heart in a staggered, continuous pattern
      if (heart2Element && heart3Element && heart4Element) {
        // Set initial state for the floating hearts
        gsap.set([heart2Element, heart3Element, heart4Element], { 
          autoAlpha: 0,
          y: 0,
          scale: 0.8
        });
      
        // Create a master timeline that repeats
        const masterTl = gsap.timeline({ repeat: -1 });
        
        // Function to create a single heart animation
        const createHeartAnim = (heartElement: SVGPathElement, delay: number) => {
          const tl = gsap.timeline({ repeat: -1, repeatDelay: 1.5 });
          
          // Heart appears and floats up
          tl.to(heartElement, { 
            autoAlpha: 1, 
            scale: 0.9,
            duration: 0.3, 
            ease: "power1.out" 
          })
          .to(heartElement, { 
            y: -40, 
            scale: 0.65,
            duration: 1.5,
            ease: "power1.out" 
          }, "<")
          .to(heartElement, { 
            autoAlpha: 0,
            duration: 0.6,
            ease: "power1.in" 
          }, "-=0.7");
          
          // Delay the start of this timeline
          return tl.delay(delay);
        };
        
        // Create and add individual heart animations with staggered starts
        masterTl.add(createHeartAnim(heart2Element, 0));
        masterTl.add(createHeartAnim(heart3Element, 0.8), "<");
        masterTl.add(createHeartAnim(heart4Element, 1.6), "<");
        
        return masterTl;
      }
      
      return null;
    };
    
    const heartsTl = animateHearts();
    
    // Cleanup animation on unmount or page change
    return () => {
      if (heart1Element) {
        gsap.killTweensOf(heart1Element);
      }
      if (heartsTl) heartsTl.kill();
    };
  }, [isInitialized]);


  return (
    <section className="relative p-9 mt-14 text-xl text-black bg-[#25F4EE] rounded-xl max-md:p-5 max-md:mt-14 ">
      <svg className="overflow-visible !absolute !m-0 top-[-45px] -right-5" id="hearts" width="105" height="70" viewBox="0 0 125 99" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path ref={heartPath1Ref} d="M40.156 39.9138C47.3776 32.6254 57.534 33.3387 63.5512 40.9042C69.6977 48.6321 68.9618 60.3919 61.9071 67.1799L38.9907 89.2297C38.4499 89.75 37.7421 90.0139 37.0243 89.9628C36.3065 89.9117 35.6379 89.5498 35.1667 88.9574L15.2008 63.8544C9.05436 56.1265 9.79021 44.3666 16.845 37.5786C23.7514 30.9334 33.9065 31.6663 40.156 39.9138ZM36.1659 59.5287C32.059 54.3652 48.3501 39.0991 43.2395 44.8594L43.1649 44.9435L35.5932 82.1411L36.5472 44.4723L36.4837 44.3784C32.1361 37.9447 52.7795 57.4879 48.0658 62.0233C43.2443 66.6625 34.6146 79.0932 38.8153 84.3748L35.5932 82.1411L36.9503 61.2319C41.7718 56.5927 40.3666 64.8102 36.1659 59.5287Z" fill="#FE2C55" />
        <path ref={heartPath2Ref} d="M79.2691 13.9678C83.5064 9.69131 89.4658 10.1098 92.9964 14.5489C96.6029 19.0833 96.1711 25.9836 92.0317 29.9665L78.5853 42.9044C78.268 43.2097 77.8527 43.3645 77.4315 43.3345C77.0103 43.3045 76.618 43.0922 76.3415 42.7446L64.6264 28.0152C61.0199 23.4808 61.4516 16.5805 65.5911 12.5976C69.6435 8.69846 75.6021 9.12851 79.2691 13.9678ZM76.9278 25.477C74.5181 22.4473 84.0771 13.4898 81.0783 16.8697L81.0346 16.919L76.5918 38.7451L77.1516 16.6426L77.1143 16.5875C74.5633 12.8124 86.676 24.2796 83.9102 26.9408C81.0812 29.6629 76.0176 36.9567 78.4824 40.0557L76.5918 38.7451L77.3881 26.4764C80.2172 23.7543 79.3926 28.576 76.9278 25.477Z" fill="#FE2C55" fill-opacity="0.7" />
        <path ref={heartPath3Ref} d="M106.571 4.27645C108.714 2.11357 111.728 2.32525 113.513 4.57035C115.337 6.86368 115.119 10.3535 113.026 12.3679L106.225 18.9113C106.064 19.0658 105.854 19.1441 105.641 19.1289C105.428 19.1137 105.23 19.0064 105.09 18.8305L99.165 11.381C97.341 9.0877 97.5594 5.59786 99.653 3.58347C101.703 1.61144 104.716 1.82894 106.571 4.27645ZM105.387 10.0973C104.168 8.56502 109.002 4.0347 107.486 5.7441L107.464 5.76906L105.217 16.8078L105.5 5.62923L105.481 5.60136C104.191 3.6921 110.317 9.4917 108.918 10.8376C107.487 12.2144 104.926 15.9033 106.173 17.4706L105.217 16.8078L105.619 10.6028C107.05 9.22607 106.633 11.6647 105.387 10.0973Z" fill="#FE2C55" fill-opacity="0.5" />
        <path ref={heartPath4Ref} d="M79.2691 13.9678C83.5064 9.69131 89.4658 10.1098 92.9964 14.5489C96.6029 19.0833 96.1711 25.9836 92.0317 29.9665L78.5853 42.9044C78.268 43.2097 77.8527 43.3645 77.4315 43.3345C77.0103 43.3045 76.618 43.0922 76.3415 42.7446L64.6264 28.0152C61.0199 23.4808 61.4516 16.5805 65.5911 12.5976C69.6435 8.69846 75.6021 9.12851 79.2691 13.9678ZM76.9278 25.477C74.5181 22.4473 84.0771 13.4898 81.0783 16.8697L81.0346 16.919L76.5918 38.7451L77.1516 16.6426L77.1143 16.5875C74.5633 12.8124 86.676 24.2796 83.9102 26.9408C81.0812 29.6629 76.0176 36.9567 78.4824 40.0557L76.5918 38.7451L77.3881 26.4764C80.2172 23.7543 79.3926 28.576 76.9278 25.477Z" fill="#FE2C55" fill-opacity="0.4" />
      </svg>
      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        <label htmlFor="email" className="self-start max-md:max-w-full text-base break-keep">
          To access it, please enter your e-mail
        </label>
        <div className="flex flex-col gap-2">
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setError(""); // Clear error when user types
            }}
            className={`flex shrink-0 bg-white text-black focus:outline-none rounded-lg h-16 max-md:max-w-full px-4 placeholder:text-sm placeholder:text-black ${error ? "border-2 border-red-500" : ""
              }`}
            placeholder="Email..."
            required
          />
          {error && (
            <span className="text-red-500 text-xl">{error}</span>
          )}
        </div>
        <button
          type="submit"
          className="bg-black text-white rounded-lg h-12 w-fit px-6 self-center text-base font-semibold hover:opacity-7 transition-colors"
        >
          Submit
        </button>
      </form>
    </section>
  );
};